import { useMemo } from 'react'
import useSWR from 'swr'
import api, { fetcher } from '../utils/api'

export const REGIONS_KEY = 'regions-v2/get_regions/'

export const districtsByRegionKey = (regionName) => {
  const name = String(regionName || '').trim()
  if (!name) return null
  return `regions-v2/get_districts_by_region/?region=${encodeURIComponent(name)}`
}

export const communitiesByDistrictKey = (districtName) => {
  const name = String(districtName || '').trim()
  if (!name) return null
  return `districts-v2/get_communities_by_district/?district=${encodeURIComponent(name)}`
}

const normalizeName = (value) => String(value || '').trim().toLowerCase()

const toNameList = (items) => {
  if (!Array.isArray(items)) return []
  return items
    .map((item) => (typeof item === 'string' ? item : item?.name))
    .filter(Boolean)
}

/**
 * Fetch districts for a region name (v2).
 * @param {string} regionName
 * @returns {Promise<Array<{ id?: number, name: string }>>}
 */
export const fetchDistrictsByRegion = async (regionName) => {
  const key = districtsByRegionKey(regionName)
  if (!key) return []
  const { data } = await api.get(key)
  return Array.isArray(data?.districts) ? data.districts : []
}

/**
 * Fetch community names for a district name (v2).
 * @param {string} districtName
 * @returns {Promise<string[]>}
 */
export const fetchCommunitiesByDistrict = async (districtName) => {
  const key = communitiesByDistrictKey(districtName)
  if (!key) return []
  const { data } = await api.get(key)
  return toNameList(data?.communities)
}

/**
 * Shared Ghana location helpers from regions-v2 / districts-v2 / communities-v2.
 */
export const useLocations = (options = {}) => {
  const { enabled = true, ...swrOptions } = options

  const { data, error, isLoading, isValidating, mutate } = useSWR(
    enabled ? REGIONS_KEY : null,
    fetcher,
    {
      revalidateOnFocus: false,
      dedupingInterval: 60_000,
      ...swrOptions,
    }
  )

  const regions = useMemo(() => {
    const list = Array.isArray(data?.regions)
      ? data.regions
      : Array.isArray(data)
        ? data
        : Array.isArray(data?.results)
          ? data.results
          : []
    return [...list].sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || '')))
  }, [data])

  /** Region names for <select> options (sorted). */
  const regionNames = useMemo(
    () => regions.map((region) => region.name).filter(Boolean),
    [regions]
  )

  const findRegion = (regionName) => {
    const target = normalizeName(regionName)
    if (!target) return null
    return regions.find((region) => normalizeName(region.name) === target) || null
  }

  return {
    regions,
    regionNames,
    findRegion,
    fetchDistrictsByRegion,
    fetchCommunitiesByDistrict,
    /** @deprecated Prefer useLocationCascade or fetchDistrictsByRegion */
    getDistricts: fetchDistrictsByRegion,
    /** @deprecated Prefer useLocationCascade or fetchCommunitiesByDistrict */
    getCommunities: (districtName) => fetchCommunitiesByDistrict(districtName),
    getDistrictNames: async (regionName) =>
      (await fetchDistrictsByRegion(regionName)).map((d) => d.name).filter(Boolean),
    isLoading,
    isValidating,
    error,
    mutate,
  }
}

/**
 * Cascading region → district → community options for forms/filters.
 * Uses:
 * - GET regions-v2/get_regions/
 * - GET regions-v2/get_districts_by_region/?region=...
 * - GET districts-v2/get_communities_by_district/?district=...
 *
 * @param {string} [region]
 * @param {string} [district]
 */
export const useLocationCascade = (region, district, options = {}) => {
  const { enabled = true, ...swrOptions } = options
  const locations = useLocations({ enabled, ...swrOptions })

  const {
    data: districtsData,
    error: districtsError,
    isLoading: districtsLoading,
    isValidating: districtsValidating,
    mutate: mutateDistricts,
  } = useSWR(
    enabled ? districtsByRegionKey(region) : null,
    fetcher,
    {
      revalidateOnFocus: false,
      dedupingInterval: 60_000,
      ...swrOptions,
    }
  )

  const {
    data: communitiesData,
    error: communitiesError,
    isLoading: communitiesLoading,
    isValidating: communitiesValidating,
    mutate: mutateCommunities,
  } = useSWR(
    enabled ? communitiesByDistrictKey(district) : null,
    fetcher,
    {
      revalidateOnFocus: false,
      dedupingInterval: 60_000,
      ...swrOptions,
    }
  )

  const districts = useMemo(
    () => (Array.isArray(districtsData?.districts) ? districtsData.districts : []),
    [districtsData]
  )

  const districtNames = useMemo(
    () => districts.map((item) => item.name).filter(Boolean),
    [districts]
  )

  /** Community names (strings) — matches previous form consumers. */
  const communities = useMemo(
    () => toNameList(communitiesData?.communities),
    [communitiesData]
  )

  return {
    ...locations,
    districts,
    districtNames,
    communities,
    isLoading: Boolean(locations.isLoading || (region && districtsLoading) || (district && communitiesLoading)),
    isValidating: Boolean(
      locations.isValidating || districtsValidating || communitiesValidating
    ),
    error: locations.error || districtsError || communitiesError,
    mutateDistricts,
    mutateCommunities,
  }
}

export default useLocations
